import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import './AiModelSettings.css';
import ModelPicker from './ModelPicker';
import { SPEND_CHART_LAYOUT } from './aiSpendChartLayout';

const AXIS_TEXT = 'rgba(226,232,240,0.78)';
const GRID_LINE = 'rgba(255,255,255,0.06)';
const SERIES_COLORS = ['#60a5fa', '#f472b6', '#34d399', '#fbbf24', '#a78bfa', '#fb7185', '#22d3ee', '#94a3b8'];
const MAX_CHART_SERIES = 7;
const CUSTOM_PRESET = 'custom';
const FEATURE_LABELS = {
  'coach.ask': 'Coach chat', 'coach.ask.tool_followup': 'Coach chat', 'coach.ask.regenerate': 'Coach chat', 'coach.ask.regenerate.correction': 'Coach chat', 'recommendation.daily': 'Daily recommendations',
  'meal.suggestion': 'Meal suggestions', 'workout.exercise_recs': 'Workout suggestions', 'workout.single_exercise_rec': 'Exercise suggestions',
  'plan.generate': 'Plan generation', 'strategy.generate': 'Strategy', 'strategy.review': 'Strategy review',
  'vision.inference': 'Photo analysis', 'food.infer': 'Food photos', 'dojo.announcement': 'Dojo announcements'
};
const labelForFeature = feature => FEATURE_LABELS[feature] || feature;
const formatUsd = value => {
  const amount = Number(value) || 0;
  if (amount === 0) return '$0';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
};
const modelFitsRole = (model, requires = {}) =>
  (!requires.image || model.capabilities?.image)
  && (!requires.tools || model.capabilities?.tools)
  && (!requires.json || model.capabilities?.json)
  && (!requires.minOutputTokens || (model.max_output_tokens || 0) >= requires.minOutputTokens);

const providerModelId = (providerId, modelId) => `user-provider:${providerId}:${modelId}`;
/** Flattens saved providers into catalog-shaped options for the per-task pickers. */
const customModelOptions = (providers = []) => providers.flatMap(provider =>
  (provider.models || []).map(model => ({
    id: providerModelId(provider.id, model.id),
    name: `${provider.name} · ${model.name || model.id}`,
    provider_name: provider.name,
    upstream_model_id: model.id,
    capabilities: model.capabilities || {},
    reasoning_control: model.reasoning_control || 'auto',
    reasoning_requirement: model.reasoning_requirement || 'optional',
    context_length: model.context_length || 0,
    max_output_tokens: model.max_output_tokens || 0,
    custom_provider: true,
  }))
);

const EMPTY_PROVIDER = { name: '', base_url: '', api_key: '' };
const EMPTY_MODEL = { id: '', name: '', capabilities: { tools: true, json: true, image: false }, reasoning_control: 'auto', reasoning_requirement: 'optional', context_length: 32768, max_output_tokens: 16384 };
// Password inputs cause browsers to treat the preceding provider URL as a
// username and the API key as a site password. Keep these fields explicitly
// outside login autofill while CSS preserves secret masking on screen.
const PROVIDER_CREDENTIAL_INPUT_PROPS = {
  autoComplete: 'off',
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
  'data-1p-ignore': 'true',
  'data-lpignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
};

function AiModelSettings({ backendService, readOnly = false, establishedBy = null, onViewEstablished, onSettingsChange, onInitialReady }) {
  const [config, setConfig] = useState(null);
  const [modelOptions, setModelOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [balance, setBalance] = useState(null);
  const [preset, setPreset] = useState('balanced');
  const [overrides, setOverrides] = useState({});
  const [providers, setProviders] = useState([]);
  const [showProviderPanel, setShowProviderPanel] = useState(false);
  const [days, setDays] = useState(30);
  const [loadError, setLoadError] = useState(null);
  const [message, setMessage] = useState(null);
  const [providerDraft, setProviderDraft] = useState(EMPTY_PROVIDER);
  const [modelDrafts, setModelDrafts] = useState({});
  const [editingProviderId, setEditingProviderId] = useState(null);
  const [providerEditDraft, setProviderEditDraft] = useState(EMPTY_PROVIDER);
  const [editingModel, setEditingModel] = useState(null);
  const [busy, setBusy] = useState(null);
  const [testResults, setTestResults] = useState({});

  const emitDraft = useCallback((nextPreset, nextOverrides, nextProviders) =>
    onSettingsChange?.({ preset: nextPreset, overrides: nextOverrides, providers: nextProviders }), [onSettingsChange]);

  const notify = (type, text) => setMessage({ type, text });
  const modelDraftFor = providerId => modelDrafts[providerId] || EMPTY_MODEL;
  const setModelDraft = (providerId, patch) =>
    setModelDrafts(previous => ({ ...previous, [providerId]: { ...modelDraftFor(providerId), ...patch } }));

  useEffect(() => {
    let cancelled = false;
    Promise.all([backendService.fetchLlmModels(), backendService.fetchLlmProviders()])
      .then(([data, customProviders]) => {
        if (cancelled) return;
        const nextPreset = data.settings?.preset || data.default_preset;
        const nextOverrides = data.settings?.overrides || {};
        setConfig(data);
        setPreset(nextPreset);
        setOverrides(nextOverrides);
        setProviders(customProviders || []);
        if (nextPreset === CUSTOM_PRESET) setShowProviderPanel(true);
        onSettingsChange?.({ preset: nextPreset, overrides: nextOverrides, providers: customProviders || [] });
      })
      .catch(error => { if (!cancelled) setLoadError(error.message || 'Could not load AI model options'); })
      .finally(() => { if (!cancelled) onInitialReady?.(); });
    return () => { cancelled = true; };
  }, [backendService, onSettingsChange, onInitialReady]);

  useEffect(() => {
    let cancelled = false;
    backendService.fetchLlmUsage(days).then(data => { if (!cancelled) setUsage(data); }).catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
  }, [backendService, days]);

  useEffect(() => {
    let cancelled = false;
    backendService.fetchLlmBalance().then(data => { if (!cancelled) setBalance(data); }).catch(() => { if (!cancelled) setBalance(null); });
    return () => { cancelled = true; };
  }, [backendService]);

  // The per-task pickers only exist under the Custom preset, so the large
  // catalog is fetched the moment Custom is in play and not before.
  const needsModelList = preset === CUSTOM_PRESET;
  useEffect(() => {
    if (!needsModelList || modelOptions) return undefined;
    let cancelled = false;
    backendService.fetchLlmModelOptions()
      .then(models => { if (!cancelled) setModelOptions(models); })
      .catch(error => { if (!cancelled) setOptionsError(error.message || 'Could not load the model list'); });
    return () => { cancelled = true; };
  }, [backendService, needsModelList, modelOptions]);

  const locked = readOnly || !config?.can_customise;

  const selectPreset = presetId => {
    // Per-task choices belong exclusively to Custom. Keeping them when the
    // user selects a named mix makes a visually selected preset lie about the
    // model that will actually serve each task.
    const nextOverrides = presetId === CUSTOM_PRESET ? overrides : {};
    setPreset(presetId);
    setOverrides(nextOverrides);
    if (presetId === CUSTOM_PRESET) setShowProviderPanel(true);
    emitDraft(presetId, nextOverrides, providers);
  };

  const setOverride = (roleId, modelId) => {
    const next = { ...overrides };
    if (!modelId) delete next[roleId]; else next[roleId] = modelId;
    setOverrides(next);
    emitDraft(preset, next, providers);
  };

  const applyProviders = (next, successText, clearedModelId = null) => {
    setProviders(next);
    // A removed provider must not leave a dangling per-task selection behind.
    const liveIds = new Set(customModelOptions(next).map(model => model.id));
    const nextOverrides = Object.fromEntries(Object.entries(overrides)
      .filter(([, modelId]) => (
        String(modelId) !== String(clearedModelId)
        && (!String(modelId).startsWith('user-provider:') || liveIds.has(String(modelId)))
      )));
    setOverrides(nextOverrides);
    emitDraft(preset, nextOverrides, next);
    if (successText) notify('success', successText);
  };

  const addProvider = async () => {
    const { name, base_url, api_key } = providerDraft;
    if (!name.trim() || !base_url.trim() || !api_key.trim()) {
      return notify('error', 'Enter a provider name, HTTPS base URL and API key.');
    }
    setBusy('add-provider');
    try {
      const next = await backendService.addLlmProvider({ name: name.trim(), base_url: base_url.trim(), api_key: api_key.trim() });
      setProviderDraft(EMPTY_PROVIDER);
      applyProviders(next, `${name.trim()} added. Now add at least one model to it.`);
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const removeProvider = async provider => {
    setBusy(`remove-provider:${provider.id}`);
    try {
      applyProviders(await backendService.deleteLlmProvider(provider.id), `${provider.name} removed.`);
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const startProviderEdit = provider => {
    setEditingProviderId(provider.id);
    setProviderEditDraft({ name: provider.name, base_url: provider.base_url, api_key: '' });
  };

  const saveProviderEdit = async provider => {
    const name = providerEditDraft.name.trim();
    const base_url = providerEditDraft.base_url.trim();
    if (!name || !base_url) return notify('error', 'Enter a provider name and HTTPS base URL.');
    const updates = { name, base_url };
    if (providerEditDraft.api_key.trim()) updates.api_key = providerEditDraft.api_key.trim();
    setBusy(`edit-provider:${provider.id}`);
    try {
      const next = await backendService.updateLlmProvider(provider.id, updates);
      setEditingProviderId(null);
      setProviderEditDraft(EMPTY_PROVIDER);
      applyProviders(next, `${name} updated.`);
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const addModel = async provider => {
    const draft = modelDraftFor(provider.id);
    if (!draft.id.trim()) return notify('error', 'Enter the model ID exactly as the provider publishes it.');
    setBusy(`add-model:${provider.id}`);
    try {
      const next = await backendService.addLlmProviderModel(provider.id, {
        id: draft.id.trim(),
        name: draft.name.trim() || draft.id.trim(),
        capabilities: draft.capabilities,
        reasoning_control: draft.reasoning_control,
        reasoning_requirement: draft.reasoning_requirement,
        context_length: draft.context_length,
        max_output_tokens: draft.max_output_tokens,
      });
      setModelDrafts(previous => ({ ...previous, [provider.id]: EMPTY_MODEL }));
      setModelOptions(null);
      applyProviders(next, `${draft.id.trim()} is now selectable for each task.`);
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const removeModel = async (provider, model) => {
    setBusy(`remove-model:${provider.id}:${model.id}`);
    try {
      setModelOptions(null);
      applyProviders(await backendService.deleteLlmProviderModel(provider.id, model.id), `${model.id} removed.`);
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const startModelEdit = (provider, model) => {
    setEditingModel({ providerId: provider.id, modelId: model.id });
    setModelDraft(provider.id, {
      ...model,
      capabilities: { tools: true, json: true, image: false, ...(model.capabilities || {}) },
    });
  };

  const saveModelEdit = async provider => {
    const draft = modelDraftFor(provider.id);
    const modelId = editingModel?.modelId;
    const currentModel = (provider.models || []).find(model => model.id === modelId);
    const capabilitiesChanged = JSON.stringify(currentModel?.capabilities || {}) !== JSON.stringify(draft.capabilities || {});
    const updates = {
      name: draft.name.trim() || modelId,
      capabilities: draft.capabilities,
      reasoning_control: draft.reasoning_control,
      reasoning_requirement: draft.reasoning_requirement,
      context_length: draft.context_length,
      max_output_tokens: draft.max_output_tokens,
    };
    setBusy(`edit-model:${provider.id}:${modelId}`);
    try {
      setModelOptions(null);
      const next = await backendService.updateLlmProviderModel(provider.id, modelId, updates);
      setEditingModel(null);
      setModelDrafts(previous => ({ ...previous, [provider.id]: EMPTY_MODEL }));
      applyProviders(
        next,
        `${draft.name.trim() || modelId} updated.${capabilitiesChanged ? ' Per-task assignments using this model were reset.' : ''}`,
        capabilitiesChanged ? `user-provider:${provider.id}:${modelId}` : null
      );
    } catch (error) {
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  /** Tests a saved provider, or the draft the user is still typing. */
  const testProvider = async (provider, modelId) => {
    const key = provider ? `${provider.id}:${modelId || modelDraftFor(provider.id).id.trim()}` : 'draft';
    setBusy(`test:${key}`);
    setMessage(null);
    try {
      const payload = provider
        ? { id: provider.id, name: provider.name, base_url: provider.base_url }
        : { name: providerDraft.name.trim(), base_url: providerDraft.base_url.trim(), api_key: providerDraft.api_key.trim() };
      const result = await backendService.testLlmProvider(payload, modelId || modelDraftFor(provider?.id || 'draft').id.trim() || undefined);
      setTestResults(previous => ({ ...previous, [key]: { ok: true, text: `Connected using ${result.model}` } }));
      notify('success', `${provider?.name || payload.name || 'Provider'} connected using ${result.model}.`);
    } catch (error) {
      setTestResults(previous => ({ ...previous, [key]: { ok: false, text: error.message } }));
      notify('error', error.message);
    } finally {
      setBusy(null);
    }
  };

  const customOptions = useMemo(() => customModelOptions(providers), [providers]);
  // Server-listed provider models carry placeholder $0 prices; the live
  // provider state is the source of truth for those ids.
  const catalogOptions = useMemo(() => (modelOptions || []).filter(model => !model.custom_provider), [modelOptions]);
  const knownModels = useMemo(
    () => new Map([...catalogOptions, ...customOptions].map(model => [model.id, model])),
    [catalogOptions, customOptions]
  );

  /**
   * What a task left on Default actually runs: the Custom preset's baseline,
   * which the server keeps equal to Balanced. `effective` is not used because
   * it already includes this user's override.
   */
  const inheritedModelFor = roleId => {
    const fallbackPreset = config?.presets?.find(option => option.id === config.default_preset);
    const customBaseline = config?.presets?.find(option => option.id === CUSTOM_PRESET)?.models?.[roleId];
    const fallbackModel = fallbackPreset?.models?.[roleId];
    const modelId = customBaseline || fallbackModel || null;
    return {
      id: modelId,
      model: modelId ? knownModels.get(modelId) || null : null,
      source: !customBaseline || customBaseline === fallbackModel ? (fallbackPreset?.label || 'Balanced') : 'Custom preset',
    };
  };

  const chartOption = useMemo(() => {
    if (!usage?.series?.length) return null;
    const totals = new Map();
    usage.series.forEach(day => Object.entries(day.by_feature || {}).forEach(([feature, cost]) => {
      const label = labelForFeature(feature);
      totals.set(label, (totals.get(label) || 0) + cost);
    }));
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([label]) => label);
    const shown = ranked.slice(0, MAX_CHART_SERIES);
    const names = ranked.length > MAX_CHART_SERIES ? [...shown, 'Other'] : shown;
    const series = names.map((name, index) => ({
      name, type: 'bar', stack: 'cost', itemStyle: { color: SERIES_COLORS[index % SERIES_COLORS.length] },
      data: usage.series.map(day => Object.entries(day.by_feature || {}).reduce((sum, [feature, cost]) =>
        sum + ((shown.includes(labelForFeature(feature)) ? labelForFeature(feature) : 'Other') === name ? cost : 0), 0)),
    }));
    return {
      backgroundColor: 'transparent',
      // Reserve wrapped legend rows plus a deliberate gap below the x-axis.
      // The taller canvas preserves usable plot height on narrow modals.
      grid: { left: 52, right: 12, top: 12, bottom: SPEND_CHART_LAYOUT.gridBottom },
      legend: {
        bottom: SPEND_CHART_LAYOUT.legendBottom,
        left: 52,
        right: 12,
        itemGap: 10,
        textStyle: { color: AXIS_TEXT, fontSize: 10 },
        itemWidth: 10,
        itemHeight: 10,
      },
      tooltip: {
        trigger: 'axis', axisPointer: { type: 'shadow' },
        formatter: params => `${params[0].axisValue}<br/>${params.filter(p => p.value > 0).map(p => `${p.marker} ${p.seriesName}: ${formatUsd(p.value)}`).join('<br/>')}<br/><b>Total: ${formatUsd(params.reduce((sum, p) => sum + (p.value || 0), 0))}</b>`,
      },
      xAxis: { type: 'category', data: usage.series.map(day => day.day.slice(5)), axisLabel: { color: AXIS_TEXT, fontSize: 10 }, axisLine: { lineStyle: { color: GRID_LINE } } },
      yAxis: { type: 'value', axisLabel: { color: AXIS_TEXT, fontSize: 10, formatter: formatUsd }, splitLine: { lineStyle: { color: GRID_LINE } } },
      series,
    };
  }, [usage]);

  if (loadError) {
    return <div className="ai-models-section"><div className="section-title">AI Models</div><p className="ai-models-error">{loadError}</p></div>;
  }
  if (!config) {
    return <div className="ai-models-section"><div className="section-title">AI Models</div><p className="ai-models-hint">Loading model options…</p></div>;
  }

  const draftTest = testResults.draft;

  return (
    <div className={`ai-models-section ${readOnly ? 'ai-models-readonly' : ''}`}>
      <div className="section-title">AI Models</div>
      {readOnly && (
        <p className="ai-settings-established-notice">
          🔒 Established by {establishedBy || 'your dojo'} — members share these AI model settings.{' '}
          <button type="button" className="ai-settings-link" onClick={onViewEstablished}>View dojo settings</button>
        </p>
      )}
      <p className="ai-models-hint">Choose how much you spend on AI. Cheaper models cost less per message but give blunter coaching.</p>
      {!readOnly && !config.can_customise && <p className="ai-models-locked">Model selection is a Pro feature.</p>}

      <div className="ai-preset-list">
        {config.presets.map(option => (
          <button
            key={option.id}
            type="button"
            disabled={locked}
            className={`ai-preset-btn ${preset === option.id ? 'selected' : ''} ${option.id === CUSTOM_PRESET ? 'ai-preset-btn-custom' : ''}`}
            onClick={() => selectPreset(option.id)}
          >
            <span className="ai-preset-label">
              {option.label}
              {option.id === CUSTOM_PRESET && customOptions.length > 0 && (
                <span className="ai-preset-tag">{customOptions.length} of your models</span>
              )}
            </span>
            <span className="ai-preset-desc">{option.description}</span>
          </button>
        ))}
      </div>

      {preset === CUSTOM_PRESET && (
        <div className="ai-custom-panel">
          <div className="ai-custom-block">
            <h4 className="ai-custom-title">Model for each task</h4>
            <p className="ai-models-hint">
              Tasks left on Default use the Balanced model. Search by name, provider or ID; sort by name or input price
              per 1M tokens. Models from your own provider are billed by it and sort after priced models.
              {!locked && ' Changes apply when you save.'}
            </p>
            {optionsError && <p className="ai-models-error">{optionsError}</p>}
            {!modelOptions && !optionsError && <p className="ai-models-hint">Loading model list…</p>}
            {modelOptions && config.roles.map(role => {
              const mine = customOptions.filter(model => modelFitsRole(model, role.requires));
              const catalog = catalogOptions.filter(model => modelFitsRole(model, role.requires));
              const selected = overrides[role.id] || '';
              return (
                <div key={role.id} className="ai-role-row">
                  <div className="ai-role-label" id={`ai-role-${role.id}-label`}>
                    {role.label}<span className="ai-role-desc">{role.description}</span>
                  </div>
                  <ModelPicker
                    id={`ai-role-${role.id}`}
                    label={role.label}
                    labelledBy={`ai-role-${role.id}-label`}
                    models={[...mine, ...catalog]}
                    value={selected}
                    currentModel={selected ? knownModels.get(selected) || null : null}
                    defaultModel={inheritedModelFor(role.id)}
                    disabled={locked}
                    onChange={modelId => setOverride(role.id, modelId)}
                  />
                  {mine.length === 0 && customOptions.length > 0 && (
                    <span className="ai-role-note">None of your models meet this task's requirements.</span>
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly && (
            <div className="ai-custom-block">
              <button type="button" className="ai-advanced-toggle" onClick={() => setShowProviderPanel(value => !value)}>
                {showProviderPanel ? 'Hide your own providers' : 'Add your own provider'}
              </button>

              {showProviderPanel && (
                <div className="ai-provider-panel">
                  <p className="ai-models-hint">
                    Connect any OpenAI-compatible endpoint. Your key is encrypted and never shown again — Kata.fit calls the
                    provider for you.
                  </p>

                  {providers.length > 0 && (
                    <ol className="ai-provider-list">
                      {providers.map(provider => {
                        const draft = modelDraftFor(provider.id);
                        const isEditingModel = editingModel?.providerId === provider.id;
                        return (
                          <li className="ai-provider-card" key={provider.id}>
                            <div className="ai-provider-card-head">
                              <div className="ai-provider-identity">
                                <strong className="ai-provider-name">{provider.name}</strong>
                                <code className="ai-provider-url">{provider.base_url}</code>
                              </div>
                              <div className="ai-provider-badges">
                                <span className={`ai-provider-badge ${provider.api_key_configured ? 'ok' : 'warn'}`}>
                                  {provider.api_key_configured ? 'Key saved' : 'No key'}
                                </span>
                                <span className={`ai-provider-badge ${provider.models?.length ? 'ok' : 'warn'}`}>
                                  {provider.models?.length || 0} model{provider.models?.length === 1 ? '' : 's'}
                                </span>
                                <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={() => startProviderEdit(provider)}>Edit provider</button>
                              </div>
                            </div>

                            {editingProviderId === provider.id && (
                              <div className="ai-subform ai-edit-form">
                                <span className="ai-subform-title">Edit provider</span>
                                <div className="ai-field-grid">
                                  <label className="ai-field"><span>Provider name</span><input aria-label={`Provider name for ${provider.name}`} className="ai-provider-input" value={providerEditDraft.name} onChange={event => setProviderEditDraft(previous => ({ ...previous, name: event.target.value }))} /></label>
                                  <label className="ai-field"><span>HTTPS base URL</span><input {...PROVIDER_CREDENTIAL_INPUT_PROPS} aria-label={`Base URL for ${provider.name}`} className="ai-provider-input ph-no-capture" data-llm-provider-credential-part="endpoint" value={providerEditDraft.base_url} onChange={event => setProviderEditDraft(previous => ({ ...previous, base_url: event.target.value }))} /><small>Use the API base, not /chat/completions. OpenAI: https://api.openai.com/v1</small></label>
                                  <label className="ai-field"><span>New API key (optional)</span><input {...PROVIDER_CREDENTIAL_INPUT_PROPS} aria-label={`New API key for ${provider.name}`} className="ai-provider-input ai-provider-secret-input ph-no-capture" type="text" data-llm-provider-credential-part="secret" data-llm-provider-secret="true" placeholder="Leave blank to keep saved key" value={providerEditDraft.api_key} onChange={event => setProviderEditDraft(previous => ({ ...previous, api_key: event.target.value }))} /></label>
                                </div>
                                <div className="ai-subform-actions">
                                  <button type="button" className="ai-primary-btn" disabled={busy !== null} onClick={() => saveProviderEdit(provider)}>{busy === `edit-provider:${provider.id}` ? 'Saving…' : 'Save provider'}</button>
                                  <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={() => { setEditingProviderId(null); setProviderEditDraft(EMPTY_PROVIDER); }}>Cancel</button>
                                </div>
                              </div>
                            )}

                            {provider.models?.length > 0 ? (
                              <ul className="ai-model-list">
                                {provider.models.map(model => {
                                  const result = testResults[`${provider.id}:${model.id}`];
                                  const testKey = `test:${provider.id}:${model.id}`;
                                  return (
                                  <li className="ai-model-row" key={model.id}>
                                    <div className="ai-model-identity">
                                      <span className="ai-model-name">{model.name || model.id}</span>
                                      <code className="ai-model-id">{model.id}</code>
                                      <span className="ai-model-caps">
                                        {[model.capabilities?.tools && 'tools', model.capabilities?.json && 'JSON', model.capabilities?.image && 'images']
                                          .filter(Boolean).join(' · ') || 'text only'}
                                        {' · '}{(model.max_output_tokens || 0).toLocaleString()} out tokens
                                      </span>
                                    </div>
                                    <div className="ai-model-actions">
                                      <div className="ai-model-action-buttons">
                                        <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={() => testProvider(provider, model.id)}>
                                          {busy === testKey ? 'Testing…' : 'Test'}
                                        </button>
                                        <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={() => startModelEdit(provider, model)}>Edit model</button>
                                        <button type="button" className="ai-inline-btn danger" disabled={busy !== null} onClick={() => removeModel(provider, model)}>
                                          {busy === `remove-model:${provider.id}:${model.id}` ? 'Removing…' : 'Remove'}
                                        </button>
                                      </div>
                                      {result && <span className={`ai-model-test-result ${result.ok ? 'success' : 'error'}`} role="status">{result.text}</span>}
                                    </div>
                                  </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <p className="ai-provider-empty">No models yet. Add one below to make this provider usable.</p>
                            )}

                            <div className="ai-subform">
                              <span className="ai-subform-title">{isEditingModel ? `Edit ${editingModel.modelId}` : `Add a model to ${provider.name}`}</span>
                              <div className="ai-field-grid">
                                <label className="ai-field">
                                  <span>Model ID</span>
                                  <input
                                    className="ai-provider-input"
                                    aria-label={`Model ID for ${provider.name}`}
                                    placeholder="org/model-name"
                                    value={draft.id}
                                    disabled={isEditingModel}
                                    onChange={event => setModelDraft(provider.id, { id: event.target.value })}
                                  />
                                </label>
                                <label className="ai-field">
                                  <span>Display name (optional)</span>
                                  <input
                                    className="ai-provider-input"
                                    aria-label={isEditingModel ? `Display name for ${editingModel.modelId}` : `Display name for new model on ${provider.name}`}
                                    placeholder="Shown in the task pickers"
                                    value={draft.name}
                                    onChange={event => setModelDraft(provider.id, { name: event.target.value })}
                                  />
                                </label>
                                <label className="ai-field">
                                  <span>Reasoning-off API</span>
                                  <select
                                    className="ai-provider-input"
                                    value={draft.reasoning_control}
                                    onChange={event => setModelDraft(provider.id, { reasoning_control: event.target.value })}
                                  >
                                    <option value="auto">Auto-detect</option>
                                    <option value="qwen">Qwen / enable_thinking=false</option>
                                    <option value="openrouter">OpenRouter / reasoning disabled</option>
                                    <option value="openai">OpenAI / reasoning_effort none</option>
                                    <option value="prompt">Prompt only (no provider control)</option>
                                  </select>
                                </label>
                                <label className="ai-field">
                                  <span>Reasoning requirement</span>
                                  <select
                                    className="ai-provider-input"
                                    value={draft.reasoning_requirement}
                                    onChange={event => setModelDraft(provider.id, { reasoning_requirement: event.target.value })}
                                  >
                                    <option value="optional">Optional / can be disabled</option>
                                    <option value="required">Required / cannot be disabled</option>
                                  </select>
                                </label>
                                <label className="ai-field">
                                  <span>Context window tokens</span>
                                  <input
                                    className="ai-provider-input"
                                    type="number"
                                    min="512"
                                    value={draft.context_length}
                                    onChange={event => setModelDraft(provider.id, { context_length: Number(event.target.value) || 0 })}
                                  />
                                </label>
                                <label className="ai-field">
                                  <span>Max output tokens</span>
                                  <input
                                    className="ai-provider-input"
                                    type="number"
                                    min="256"
                                    value={draft.max_output_tokens}
                                    onChange={event => setModelDraft(provider.id, { max_output_tokens: Number(event.target.value) || 0 })}
                                  />
                                </label>
                              </div>
                              <div className="ai-cap-row">
                                <span className="ai-cap-label">This model supports</span>
                                {[['tools', 'Tool calls'], ['json', 'JSON mode'], ['image', 'Image input']].map(([capability, label]) => (
                                  <label className="ai-cap-toggle" key={capability}>
                                    <input
                                      type="checkbox"
                                      checked={capability === 'image' ? draft.capabilities?.image === true : draft.capabilities?.[capability] !== false}
                                      onChange={event => setModelDraft(provider.id, { capabilities: { ...draft.capabilities, [capability]: event.target.checked } })}
                                    />
                                    <span>{label}</span>
                                  </label>
                                ))}
                              </div>
                              <p className="ai-models-hint ai-cap-hint">Capabilities decide which tasks can use this model. Only claim what the model really does.</p>
                              <div className="ai-subform-actions">
                                <button type="button" className="ai-primary-btn" disabled={busy !== null} onClick={() => isEditingModel ? saveModelEdit(provider) : addModel(provider)}>
                                  {isEditingModel ? (busy === `edit-model:${provider.id}:${editingModel.modelId}` ? 'Saving…' : 'Save model') : (busy === `add-model:${provider.id}` ? 'Adding…' : 'Add model')}
                                </button>
                                {isEditingModel && <button type="button" className="ai-inline-btn" disabled={busy !== null} onClick={() => { setEditingModel(null); setModelDrafts(previous => ({ ...previous, [provider.id]: EMPTY_MODEL })); }}>Cancel</button>}
                                <button type="button" className="ai-inline-btn danger" disabled={busy !== null} onClick={() => removeProvider(provider)}>
                                  {busy === `remove-provider:${provider.id}` ? 'Removing…' : 'Remove provider'}
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}

                  <div className="ai-subform ai-subform-new">
                    <span className="ai-subform-title">{providers.length ? 'Connect another provider' : 'Connect your first provider'}</span>
                    <div className="ai-field-grid">
                      <label className="ai-field">
                        <span>Provider name</span>
                        <input
                          className="ai-provider-input"
                          placeholder="Featherless"
                          value={providerDraft.name}
                          onChange={event => setProviderDraft(previous => ({ ...previous, name: event.target.value }))}
                        />
                      </label>
                      <label className="ai-field">
                        <span>HTTPS base URL</span>
                        <input
                          {...PROVIDER_CREDENTIAL_INPUT_PROPS}
                          className="ai-provider-input ph-no-capture"
                          data-llm-provider-credential-part="endpoint"
                          placeholder="https://api.featherless.ai/v1"
                          value={providerDraft.base_url}
                          onChange={event => setProviderDraft(previous => ({ ...previous, base_url: event.target.value }))}
                        />
                        <small>Use the API base, not /chat/completions. OpenAI: https://api.openai.com/v1</small>
                      </label>
                      <label className="ai-field">
                        <span>API key</span>
                        <input
                          {...PROVIDER_CREDENTIAL_INPUT_PROPS}
                          className="ai-provider-input ai-provider-secret-input ph-no-capture"
                          type="text"
                          data-llm-provider-credential-part="secret"
                          data-llm-provider-secret="true"
                          placeholder="Stored encrypted"
                          value={providerDraft.api_key}
                          onChange={event => setProviderDraft(previous => ({ ...previous, api_key: event.target.value }))}
                        />
                      </label>
                    </div>
                    <div className="ai-subform-actions">
                      <button type="button" className="ai-primary-btn" disabled={busy !== null} onClick={addProvider}>
                        {busy === 'add-provider' ? 'Saving…' : 'Add provider'}
                      </button>
                      <button
                        type="button"
                        className="ai-inline-btn"
                        disabled={busy !== null || !providerDraft.base_url.trim() || !providerDraft.api_key.trim()}
                        onClick={() => testProvider(null)}
                        title="Checks the URL and key before saving"
                      >
                        {busy === 'test:draft' ? 'Testing…' : 'Test connection'}
                      </button>
                    </div>
                    <p className="ai-models-hint ai-cap-hint">Step 1: add the provider. Step 2: add its models from the card above.</p>
                    {draftTest && <p className={draftTest.ok ? 'ai-models-success' : 'ai-models-error'}>{draftTest.text}</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!readOnly && message && <p className={message.type === 'error' ? 'ai-models-error' : 'ai-models-success'}>{message.text}</p>}

      <div className="ai-spend-block">
        {balance?.metering_enabled && (
          <div className="ai-balance">
            <div className="ai-balance-main">
              <span className="ai-balance-amount">{formatUsd(balance.available_usd)}</span>
              <span className="ai-balance-label">available</span>
            </div>
          </div>
        )}
        <div className="ai-spend-header">
          <span className="ai-spend-title">Your AI spend</span>
          <div className="ai-spend-ranges">
            {[7, 30, 90].map(range => (
              <button key={range} type="button" className={`ai-range-btn ${days === range ? 'selected' : ''}`} onClick={() => setDays(range)}>{range}d</button>
            ))}
          </div>
        </div>
        {usage ? (
          <>
            <div className="ai-spend-total">
              {formatUsd(usage.total_cost)}
              <span className="ai-spend-total-sub">over {usage.days} days · {usage.total_calls} calls</span>
            </div>
            {chartOption
              ? <ReactECharts option={chartOption} style={{ height: SPEND_CHART_LAYOUT.height, width: '100%' }} notMerge />
              : <p className="ai-models-hint">No AI usage recorded yet.</p>}
          </>
        ) : <p className="ai-models-hint">Loading usage…</p>}
      </div>
    </div>
  );
}

export default AiModelSettings;
